import { useState } from "react";
import { toast } from "sonner";
import { Check, Sparkles, Wand2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Claude helpers for the article being edited. Every suggestion is a proposal
 *  the editor accepts or rejects — nothing is applied automatically. */
export function KbAiAssistPanel({ form, onApply }) {
  const [busy, setBusy] = useState("");
  const [meta, setMeta] = useState(null);
  const [polish, setPolish] = useState(null);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The assistant is unavailable"); }
    finally { setBusy(""); }
  };

  const suggestMeta = () => run("meta", async () => {
    const { data } = await api.post("/kb/ai/suggest-meta",
      { title: form.name, body_html: form.body_html });
    setMeta(data);
  });

  const doPolish = (mode) => run(mode, async () => {
    const { data } = await api.post("/kb/ai/polish",
      { body_html: form.body_html, mode, audience: (form.audiences || [])[0] || "internal" });
    setPolish(data);
  });

  return (
    <div className="space-y-3" data-testid="kb-ai-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        <Sparkles className="h-3.5 w-3.5" /> AI assist
      </div>
      <p className="text-[11px] text-slate-400 dark:text-slate-500">
        Claude Sonnet 4.6 suggests; you decide. Nothing changes until you click Apply.
      </p>

      <div className="grid grid-cols-1 gap-1.5">
        <Button size="sm" variant="outline" className="h-7 justify-start text-xs"
          data-testid="kb-ai-suggest-meta" onClick={suggestMeta} disabled={!!busy}>
          {busy === "meta" ? "Thinking…" : "Suggest tags, keywords & category"}
        </Button>
        <Button size="sm" variant="outline" className="h-7 justify-start text-xs"
          data-testid="kb-ai-tone" onClick={() => doPolish("tone")} disabled={!!busy}>
          {busy === "tone" ? "Rewriting…" : "Improve tone & clarity"}
        </Button>
        <Button size="sm" variant="outline" className="h-7 justify-start text-xs"
          data-testid="kb-ai-grammar" onClick={() => doPolish("grammar")} disabled={!!busy}>
          {busy === "grammar" ? "Checking…" : "Check grammar & spelling"}
        </Button>
      </div>

      {meta && (
        <div className="space-y-2 rounded-md border border-slate-200 dark:border-slate-800 p-2.5" data-testid="kb-ai-meta-result">
          {!!meta.tags?.length && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Tags</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {meta.tags.map((t) => (
                  <span key={t} className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">{t}</span>
                ))}
              </div>
            </div>
          )}
          {!!meta.keywords?.length && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Search keywords</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {meta.keywords.map((k) => (
                  <span key={k} className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-300">{k}</span>
                ))}
              </div>
            </div>
          )}
          {meta.meta_description && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Meta description</div>
              <p className="text-[11px] text-slate-600 dark:text-slate-300">{meta.meta_description}</p>
            </div>
          )}
          {!!meta.categories?.length && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Category</div>
              {meta.categories.map((c) => (
                <p key={c.cat_id || c.name} className="text-[11px] text-slate-600 dark:text-slate-300">
                  {c.name} <span className="text-slate-400 dark:text-slate-500">— {c.why}</span>
                </p>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <Button size="sm" className="h-6 flex-1 text-[11px]" data-testid="kb-ai-meta-apply"
              onClick={() => {
                onApply({
                  tags: [...new Set([...(form.tags || []), ...(meta.tags || [])])].slice(0, 30),
                  keywords: [...new Set([...(form.keywords || []), ...(meta.keywords || [])])].slice(0, 40),
                  meta_description: form.meta_description || meta.meta_description || "",
                  ...(meta.categories?.[0]?.cat_id ? { primary_category_id: meta.categories[0].cat_id } : {}),
                });
                setMeta(null);
                toast.success("Suggestions applied");
              }}>
              <Check className="mr-1 h-3 w-3" /> Apply
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[11px]" data-testid="kb-ai-meta-dismiss"
              onClick={() => setMeta(null)}>Dismiss</Button>
          </div>
        </div>
      )}

      {polish && (
        <div className="space-y-2 rounded-md border border-slate-200 dark:border-slate-800 p-2.5" data-testid="kb-ai-polish-result">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {polish.mode === "tone" ? "Tone & clarity" : "Grammar & spelling"}
          </div>
          <p className="text-[11px] text-slate-600 dark:text-slate-300">{polish.summary}</p>
          <ul className="max-h-40 space-y-0.5 overflow-y-auto">
            {(polish.changes || []).map((c, i) => (
              <li key={i} className="text-[11px] text-slate-500 dark:text-slate-400">· {c}</li>
            ))}
          </ul>
          <div className="flex gap-1.5">
            <Button size="sm" className="h-6 flex-1 text-[11px]" data-testid="kb-ai-polish-apply"
              onClick={() => {
                onApply({ body_html: polish.body_html });
                setPolish(null);
                toast.success("Rewrite applied — Save when you're happy with it");
              }}>
              <Wand2 className="mr-1 h-3 w-3" /> Apply rewrite
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[11px]" data-testid="kb-ai-polish-dismiss"
              onClick={() => setPolish(null)}>Dismiss</Button>
          </div>
        </div>
      )}
    </div>
  );
}
